import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  createPlacementTest, 
  resetStatus,
  selectPlacementTestCreateStatus,
  selectPlacementTestFormErrors 
} from '../../features/placementTests/placementTestSlice';
import { selectUserInformations } from '../../features/authSlice';
import SharedForm from '../../components/forms/SharedForm';
import placementTestFields from '../../config/placementTestFields';
import { showToast } from '../../utils/utils';
import axios from 'axios';
import { API } from '../../shared/api/Api';

const CreatePlacementTestPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  
  const createStatus = useSelector(selectPlacementTestCreateStatus);
  const formErrors = useSelector(selectPlacementTestFormErrors);

  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState(
    placementTestFields.reduce(
      (acc, field) => ({ ...acc, [field.name]: field.defaultValue || '' }),
      {}
    )
  );
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      type: prev.type || "pre_enrollment",
      full_marks: prev.full_marks || 100,
      passing_score: prev.passing_score || 50,
    }));
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await axios.get(API.Courses.app_courses_all, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (data.status === 200) {
          const items = data.data?.items || [];
          setCourses(items);
        }
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      }
    };
    fetchCourses();
  }, [user.token]);

  const fieldsWithOptions = placementTestFields.map(field => {
    if (field.name === 'course_id') {
      const onlyPlacementCourses =
        formData.type === "pre_enrollment"
          ? courses.filter((course) => Boolean(course.requires_placement_test))
          : courses;
      return {
        ...field,
        options: onlyPlacementCourses.map(course => ({ value: course.id, label: course.title }))
      };
    }
    return field;
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "type" && value === "standalone") {
        next.course_id = "";
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.type === "pre_enrollment" && !formData.course_id) {
      showToast("Error", "Please select a course for pre-enrollment placement tests.", "error");
      return;
    }
    if (Number(formData.full_marks) <= 0) {
      showToast("Error", "Full marks must be greater than 0.", "error");
      return;
    }
    if (Number(formData.passing_score) < 0 || Number(formData.passing_score) > 100) {
      showToast("Error", "Passing score must be between 0 and 100.", "error");
      return;
    }
    dispatch(createPlacementTest({ token: user.token, formData }));
  };

  useEffect(() => {
    if (createStatus === 'succeeded') {
      showToast('Success', 'Placement test created successfully', 'success');
      dispatch(resetStatus());
      navigate('/dashboard/placement-tests');
    }
  }, [createStatus, dispatch, navigate]);

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Create Placement Test</h1>
            <button onClick={() => navigate('/dashboard/placement-tests')} className="text-gray-500 hover:underline">Cancel</button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <SharedForm
            fields={fieldsWithOptions}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={createStatus === 'loading'}
            hasCourses={formData.type === "pre_enrollment"}
            entity="placement-test"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CreatePlacementTestPage;
